/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import * as color from '@constant/color';
import { v4 as uuid4 } from 'uuid';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as workbookActions } from '@store/slice/workbook-slice';

interface IProps {
  workbookKey: string;
  choice: string[];
  answer: number;
  isAnswerView: boolean;
  isChoiceView: boolean;
  label: 'number' | 'alphabet';
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;

const ChoiceContainer = styled.div<{
  $design: { backgroundColor: string; borderColor: string; click: boolean };
}>`
  display: flex;
  flex-flow: row nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
  padding: 5px 5px 5px 5px;
  // box-shadow: ${color.SURFACE_SHADOW};
  border-radius: ${size.BORDER_RADIUS_1};
  border: ${size.BORDER_WIDTH_1} solid
    ${(props) => (props.$design.borderColor === 'transparent' ? color.GRAY_4 : props.$design.borderColor)};
  background-color: ${(props) => props.$design.backgroundColor || 'transparent'};
  cursor: ${(props) => (props.$design.click ? 'default' : 'pointer')};

  ${(props) =>
    !props.$design.click &&
    !IS_IYAGIGO_MOBILE_VIEW &&
    `
    &:hover {
      box-shadow: none;
      border-color: ${color.GRAY_2};
      // transform: translateY(-1px);
    }
  `}
`;

const CHOICE_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left', event: { cursor: 'pointer' } },
} as const;

const ProblemChoice: React.FC<IProps> = ({ workbookKey, choice, answer, label, isAnswerView, isChoiceView }) => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const selectedChoice =
    useTypedSelector((state) => state.rootReducer?.workbookReducer?.selectedTestWorkbookChoice?.[workbookKey]) ?? null;

  // ############### function ###############
  const createBorderDesign = (isAnswerView: boolean, isAnswer: boolean, isSelected: boolean): string => {
    if (isAnswerView) {
      if (isAnswer) {
        return color.GREEN_4;
      } else if (isSelected) {
        return color.RED_4;
      }
    } else {
      if (isSelected) {
        return color.YELLOW_3;
      }
    }
    return 'transparent';
  };

  // ############### handler ###############
  const handleChoiceClick = (answer: string) => {
    if (isAnswerView) return;
    dispatch(workbookActions.selectTestChoice({ workbookKey, selectedChoice: answer }));
  };

  // ############### items ###############
  const choiceLabels = label === 'number' ? ['(1)', '(2)', '(3)', '(4)'] : ['(A)', '(B)', '(C)', '(D)'];
  const choices = choice.map((choice, idx) => {
    return {
      id: idx + 1,
      text: choice,
      // 정답의 '번호'와 보기의 '번호'가 같으면 '정답' 체크
      isAnswer: answer === idx + 1,
    };
  });

  // const choiceLabels = label === 'number' ? ['(1)', '(2)', '(3)', '(4)'] : ['(A)', '(B)', '(C)', '(D)'];

  const Choices = choices.map((item) => {
    // 선택된 보기 '번호'와 보기의 '번호'가 같으면 '선택' 체크
    const isSelected = selectedChoice === item.text;
    const label = choiceLabels[item.id - 1] ?? '';

    return (
      <ChoiceContainer
        key={uuid4()}
        onClick={() => handleChoiceClick(item.text)}
        $design={{
          borderColor: createBorderDesign(isAnswerView, item.isAnswer, isSelected),
          backgroundColor: 'transparent',
          click: isAnswerView,
        }}>
        <TextBlock text={label} design={CHOICE_TEXT_DESIGN} />
        {(isAnswerView || isChoiceView) && <TextBlock text={item.text} design={CHOICE_TEXT_DESIGN} />}
      </ChoiceContainer>
    );
  });

  return <Wrapper>{Choices}</Wrapper>;
};

export default ProblemChoice;
